import { useCallback, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getMaxLoadedPages } from '@/lib/query/get-max-loaded-pages';
import { FcmNotificationType } from '@/modules/notifications';

import { useNotifications } from './use-notifications';

interface UseFcmListRefreshParams {
  /**
   * FCM notification type that signals new data is available (e.g. INVOICE_READY).
   */
  notificationType: FcmNotificationType;

  /**
   * TanStack Query key prefix for the infinite list (e.g. ['invoices']).
   * resetQueries / getQueriesData are scoped to this prefix (exact: false).
   */
  queryKey: unknown[];

  /**
   * Whether a fetch is currently in progress for the list query.
   * Used to detect when a triggered refresh has completed so the banner can be hidden.
   * Pass `isFetching` from the corresponding useInfiniteQuery result.
   */
  isFetching: boolean;
}

export interface UseFcmListRefreshResult {
  /**
   * True when a silent push arrived and the banner should be shown.
   * Remains true until the triggered refresh completes.
   */
  hasPendingRefresh: boolean;

  /**
   * True between the moment refresh() is called and the fetch completing.
   * Use for isPending on the RefreshData button and for isLoading on the table.
   */
  isRefreshing: boolean;

  /**
   * Call this when the user clicks the refresh button.
   * Resets the query cache to page 1 and triggers a fresh fetch (identical to initial load).
   */
  refresh: () => void;
}

/**
 * Generic hook for infinite-scroll list pages that need to respond to silent FCM pushes.
 *
 * Behaviour:
 * - When a matching silent notification arrives and only 1 page is cached:
 *   resets the query immediately (silent background refresh, no banner).
 * - When more than 1 page is cached: shows the refresh banner so the user
 *   can choose when to reset to page 1.
 * - When the user clicks refresh: resets the query (page 1, full loading state)
 *   and keeps the banner + button spinner visible until the fetch completes.
 *
 * Usage:
 * ```tsx
 * const { hasPendingRefresh, isRefreshing, refresh } = useFcmListRefresh({
 *   notificationType: FCMNotificationType.INVOICE_READY,
 *   queryKey: ['invoices'],
 *   isFetching,
 * });
 *
 * // In JSX:
 * {hasPendingRefresh && (
 *   <RefreshData
 *     onRefresh={refresh}
 *     isPending={isRefreshing}
 *     dataUpdatedAt={null}
 *     description={t('refreshBanner.description')}
 *     buttonTitle={t('refreshBanner.buttonTitle')}
 *   />
 * )}
 * <MyTable isLoading={isLoading || isFetchingNextPage} ... />
 * ```
 */
export const useFcmListRefresh = ({
  notificationType,
  queryKey,
  isFetching,
}: UseFcmListRefreshParams): UseFcmListRefreshResult => {
  const [hasPendingRefresh, setHasPendingRefresh] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  // Hide the banner once the triggered reset-fetch completes
  useEffect(() => {
    if (isRefreshing && !isFetching) {
      setHasPendingRefresh(false);
      setIsRefreshing(false);
    }
  }, [isFetching, isRefreshing]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    // resetQueries clears the cache and fetches page 1 only, producing a full
    // loading state identical to the initial load
    queryClient.resetQueries({ queryKey, exact: false });
  }, [queryClient, queryKey]);

  useNotifications(notificationType, notification => {
    if (!notification.silent) return;

    // Multiple pages loaded → banner so the user resets to page 1 intentionally.
    // Single page → refresh silently without disrupting the user.
    if (getMaxLoadedPages(queryClient, queryKey) > 1) {
      setHasPendingRefresh(true);
    } else {
      refresh();
    }
  });

  return { hasPendingRefresh, isRefreshing, refresh };
};
